import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Save, ArrowLeft, Upload, Plus, Trash2, Link as LinkIcon } from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { supabase } from '../../lib/supabase';

const AdminProductEdit = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: 'cordless_stick',
    currentPrice: '',
    originalPrice: '',
    description: '',
    imageUrl: '',
    imageAlt: '',
    badge: '',
    discount: '0',
    isActive: true,
  });

  const [affiliateLinks, setAffiliateLinks] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin-login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !authLoading && productId) {
      loadProduct();
    }
  }, [user, authLoading, productId]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('products')
        ?.select('*')
        ?.eq('id', productId)
        ?.single();

      if (error) throw error;

      setFormData({
        name: data?.name || '',
        brand: data?.brand || '',
        category: data?.category || 'cordless_stick',
        currentPrice: data?.current_price?.toString() || '',
        originalPrice: data?.original_price?.toString() || '',
        description: data?.description || '',
        imageUrl: data?.image_url || '',
        imageAlt: data?.image_alt || '',
        badge: data?.badge || '',
        discount: data?.discount?.toString() || '0',
        isActive: data?.is_active ?? true,
      });

      // Parse affiliate links
      const links = typeof data?.affiliate_links === 'string'
        ? JSON.parse(data?.affiliate_links)
        : (data?.affiliate_links || []);
      setAffiliateLinks(links);

    } catch (err) {
      console.error('Error loading product:', err);
      setError('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddAffiliateLink = () => {
    setAffiliateLinks(prev => [
      ...prev,
      { retailer: '', url: '', price: 0, availability: 'in_stock' }
    ]);
  };

  const handleRemoveAffiliateLink = (index) => {
    setAffiliateLinks(prev => prev?.filter((_, i) => i !== index));
  };

  const handleAffiliateLinkChange = (index, field, value) => {
    setAffiliateLinks(prev => prev?.map((link, i) =>
      i === index ? { ...link, [field]: value } : link
    ));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validate required fields
      if (!formData?.name || !formData?.brand || !formData?.currentPrice || !formData?.description) {
        setError('Please fill in all required fields (Name, Brand, Price, Description)');
        return;
      }

      // Update product
      const { error: updateError } = await supabase
        ?.from('products')
        ?.update({
          name: formData?.name,
          brand: formData?.brand,
          category: formData?.category,
          current_price: parseFloat(formData?.currentPrice),
          original_price: parseFloat(formData?.originalPrice || formData?.currentPrice),
          description: formData?.description,
          image_url: formData?.imageUrl,
          image_alt: formData?.imageAlt || `${formData?.brand} ${formData?.name} vacuum cleaner`,
          badge: formData?.badge || null,
          discount: parseInt(formData?.discount) || 0,
          is_active: formData?.isActive,
          affiliate_links: affiliateLinks,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', productId);

      if (updateError) throw updateError;

      setSuccess('Product updated successfully!');
      setTimeout(() => {
        navigate('/admin/products');
      }, 1500);

    } catch (err) {
      console.error('Error saving product:', err);
      setError(err?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    { value: 'upright', label: 'Upright' },
    { value: 'robot', label: 'Robot' },
    { value: 'cordless_stick', label: 'Cordless Stick' },
    { value: 'canister', label: 'Canister' },
    { value: 'handheld', label: 'Handheld' },
    { value: 'stick', label: 'Stick' }
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/admin/products')}
            variant="ghost"
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Products
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Edit Product</h1>
          <p className="text-gray-600 mt-2">
            Update product information, images, and affiliate links
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-600">{success}</p>
          </div>
        )}

        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <Input
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                placeholder="e.g., V15 Detect Absolute"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand *
              </label>
              <Input
                type="text"
                value={formData?.brand}
                onChange={(e) => handleInputChange('brand', e?.target?.value)}
                placeholder="e.g., Dyson"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <Select
                value={formData?.category}
                onChange={(e) => handleInputChange('category', e?.target?.value)}
              >
                {categories?.map(cat => (
                  <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Price ($) *
              </label>
              <Input
                type="number"
                step="0.01"
                value={formData?.currentPrice}
                onChange={(e) => handleInputChange('currentPrice', e?.target?.value)}
                placeholder="299.99"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Original Price ($)
              </label>
              <Input
                type="number"
                step="0.01"
                value={formData?.originalPrice}
                onChange={(e) => handleInputChange('originalPrice', e?.target?.value)}
                placeholder="399.99"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount (%)
              </label>
              <Input
                type="number"
                value={formData?.discount}
                onChange={(e) => handleInputChange('discount', e?.target?.value)}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Badge
              </label>
              <Input
                type="text"
                value={formData?.badge}
                onChange={(e) => handleInputChange('badge', e?.target?.value)}
                placeholder="e.g., Best Seller, New Arrival"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData?.isActive}
                  onChange={(e) => handleInputChange('isActive', e?.target?.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">Active Product</span>
              </label>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Detailed product description..."
            />
          </div>
        </div>

        {/* Image Management */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Image Management
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image URL
              </label>
              <Input
                type="text"
                value={formData?.imageUrl}
                onChange={(e) => handleInputChange('imageUrl', e?.target?.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Alt Text
              </label>
              <Input
                type="text"
                value={formData?.imageAlt}
                onChange={(e) => handleInputChange('imageAlt', e?.target?.value)}
                placeholder="Descriptive alt text for accessibility"
              />
            </div>
          </div>
          {formData?.imageUrl && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
              <img
                src={formData?.imageUrl}
                alt={formData?.imageAlt || 'Product preview'}
                className="w-48 h-48 object-cover rounded-lg border border-gray-200"
                onError={(e) => {
                  e.target.src = '/assets/images/no_image.png';
                }}
              />
            </div>
          )}
        </div>

        {/* Affiliate Links */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <LinkIcon className="w-5 h-5" />
              Affiliate Links
            </h2>
            <Button
              onClick={handleAddAffiliateLink}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Link
            </Button>
          </div>
          
          {affiliateLinks?.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No affiliate links added yet. Click "Add Link" to get started.
            </p>
          ) : (
            <div className="space-y-4">
              {affiliateLinks?.map((link, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="font-medium text-gray-900">Link #{index + 1}</h3>
                    <Button
                      onClick={() => handleRemoveAffiliateLink(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Retailer
                      </label>
                      <Input
                        type="text"
                        value={link?.retailer}
                        onChange={(e) => handleAffiliateLinkChange(index, 'retailer', e?.target?.value)}
                        placeholder="e.g., Amazon"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price ($)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={link?.price}
                        onChange={(e) => handleAffiliateLinkChange(index, 'price', parseFloat(e?.target?.value))}
                        placeholder="299.99"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Affiliate URL
                      </label>
                      <Input
                        type="text"
                        value={link?.url}
                        onChange={(e) => handleAffiliateLinkChange(index, 'url', e?.target?.value)}
                        placeholder="https://retailer.com/product?ref=vacuumguide"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Availability
                      </label>
                      <Select
                        value={link?.availability}
                        onChange={(e) => handleAffiliateLinkChange(index, 'availability', e?.target?.value)}
                      >
                        <option value="in_stock">In Stock</option>
                        <option value="low_stock">Low Stock</option>
                        <option value="out_of_stock">Out of Stock</option>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            onClick={() => navigate('/admin/products')}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminProductEdit;